import Grid from '@/global/components/ui/Grid'
import { parseDataTransform, uploadFile } from '@acme/common'
import dayjs from 'dayjs'
import { SelectEmpresa } from 'global/components/Selects'
import Dialog from 'global/guides/Dialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { useAusenciasOutletContext } from 'pages/Ausencias.context'
import { SubmitHandler } from 'react-hook-form'
import {
    abonos,
    createAbonoRecorrenteSchema,
    createAbonoSchema,
    RecorrenciaAbono,
} from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import FormNovoAbono from 'templates/ManutencaoDoPonto/Digitacao/forms/NovoAbono'
import { z } from 'zod'
import { REPETICAO_DEFAULT_VALUES } from '../../Digitacao/atoms/RepeticaoAbonoAccordion'
import { useUpdateDigitacaoQueryData } from '../../Digitacao/pages/useDigitacaoData'
import LancamentosAnteriores from '../lancamentos-anteriores'
import invalidateAusenciasQuery from './invalidateAusenciasQuery'

const baseUrl = import.meta.env.VITE_BASE_URL

export default function AdicionarAbonoModal() {
    const { mutateAsync, isLoading } = abonos.mutations.useCreate()
    const { setQueryData } = useUpdateDigitacaoQueryData()
    const funcionarioId = useStore(state => state.employeeId)
    const empresaId = useStore(state => state.companyId)

    const { data } = useAusenciasOutletContext()
    const rows = data
        ?.filter(({ tipoAusencia }) => tipoAusencia === ('Abono' as any))
        ?.filter(item => item.funcionarioId == funcionarioId)

    const handleSubmit: SubmitHandler<
        z.infer<typeof createAbonoRecorrenteSchema>
    > = async ({
        fileUrl,
        every,
        repeticaoMensalOpcao,
        tipoRepeticao,
        dataFinal,
        ...data
    }) => {
        const filename = await uploadFile(fileUrl!, baseUrl)
        const json = buildAbono(
            {
                ...parseDataTransform(data),
                funcionarioId: data.funcionarioId,
                empresaId,
                fileUrl: filename,
            },
            {
                every,
                repeticaoMensalOpcao,
                tipoRepeticao,
                dataFinal,
            },
        )
        await mutateAsync(json, {
            onSuccess: async res => {
                await invalidateAusenciasQuery()
                if (!Array.isArray(res)) res = [res]
                for (const abono of res) setQueryData('ABONO', abono)
                toast.success('Abono adicionado com sucesso!')
            },
            onError: err => {
                toast.error(err.message)
            },
        })
    }

    return (
        <Dialog.Root
            onSubmit={handleSubmit}
            loading={isLoading}
            defaultValues={{
                tipo: 'NORMAL',
                funcionarioId: [funcionarioId].filter(it => it > 0),
                ...REPETICAO_DEFAULT_VALUES,
            }}
            schema={createAbonoRecorrenteSchema}
            open={hasPermission(['create', 'Abonos'])}
        >
            <Dialog.Title>Novo abono</Dialog.Title>
            <Dialog.Content>
                <Grid container>
                    <Grid item xs={12}>
                        <SelectEmpresa variant="form" />
                    </Grid>
                </Grid>
                <FormNovoAbono />
                <LancamentosAnteriores rows={rows} />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton closeOnSubmit />
            </Dialog.Actions>
        </Dialog.Root>
    )
}

/**
 * Transforma os dados do formulário em um objeto abono conforme a recorrência selecionada.
 * Baseado na lógica de TabelasHorarioFuncionarioEntity.getTrocasOrganizadas()
 * @returns Dados do abono prontos para serem enviados à API
 */
export function buildAbono<
    T extends Partial<z.infer<typeof createAbonoSchema>>,
>(
    { data, ...abono }: T,
    {
        every,
        repeticaoMensalOpcao,
        tipoRepeticao,
        dataFinal: final,
    }: RecorrenciaAbono,
) {
    const res = {
        ...abono,
        tipo: abono.tipo || 'NORMAL',
        data: data ? [dayjs(data).toDate()] : [],
    }
    if (!data) return res
    if (tipoRepeticao === 'NAO_REPETE') return res
    if (!final) return res

    const dtInicial = dayjs(data)
    const dtFinal = dayjs(final)
    const intervalo = Number(every ?? 0) + 1

    const datas: Date[] = []
    switch (tipoRepeticao) {
        case 'DIARIO': {
            for (
                let dt = dtInicial;
                dt.isSameOrBefore(dtFinal, 'day');
                dt = dt.add(intervalo, 'day')
            ) {
                datas.push(dt.toDate())
            }
            break
        }
        case 'SEMANAL': {
            const repeticaoSemanalDia = dtInicial.day()
            let primeiraData = dtInicial.day(repeticaoSemanalDia)
            if (primeiraData.isBefore(dtInicial, 'day')) {
                primeiraData = primeiraData.add(1, 'week')
            }

            for (
                let dt = primeiraData;
                dt.isSameOrBefore(dtFinal, 'day');
                dt = dt.add(intervalo, 'week')
            ) {
                datas.push(dt.toDate())
            }
            break
        }
        case 'MENSAL': {
            if (repeticaoMensalOpcao === 'DIA') {
                const diaMes = dtInicial.date()
                for (
                    let dt = dtInicial;
                    dt.isSameOrBefore(dtFinal, 'day');
                    dt = dt.add(intervalo, 'month').date(diaMes)
                ) {
                    datas.push(dt.toDate())
                }
            } else if (repeticaoMensalOpcao === 'SEMANA') {
                const diaSemana = dtInicial.day()
                const semanaDoMes = Math.ceil(dtInicial.date() / 7)

                for (
                    let dt = dtInicial;
                    dt.isSameOrBefore(dtFinal, 'day');
                    dt = dt.add(intervalo, 'month')
                ) {
                    const primeiroDiaMes = dt.date(1)
                    let dataCalculada = primeiroDiaMes.day(diaSemana)
                    if (dataCalculada.isBefore(primeiroDiaMes, 'day')) {
                        dataCalculada = dataCalculada.add(1, 'week')
                    }
                    dataCalculada = dataCalculada.add(semanaDoMes - 1, 'week')

                    if (dataCalculada.isSameOrBefore(dtFinal, 'day')) {
                        datas.push(dataCalculada.toDate())
                    }
                }
            }
            break
        }
        default:
            break
    }

    if (!datas.length) {
        datas.push(dayjs(data).toDate())
    }

    return {
        ...res,
        data: datas,
    }
}
